import React, { useState, useEffect } from "react";
import {
  Card,
  Form,
  Input,
  DatePicker,
  Button,
  message,
  Select,
  Space,
  Modal,
} from "antd";
import {
  PhoneOutlined,
  CalendarOutlined,
  UserOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  EyeOutlined,
  CloseOutlined,
  HomeOutlined,
} from "@ant-design/icons";
import { useSearchParams, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import {
  getAvailableTimeSlots,
  createCallBooking,
  getBookingStats,
  getQuoteData,
} from "../services/callBookingService";
import { logDebug, logError } from "@/shared/utils/logger.js";

const { TextArea } = Input;

const CallBookingPage = () => {
  const [searchParams] = useSearchParams();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [timeSlots, setTimeSlots] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [dateAvailability, setDateAvailability] = useState({});
  const [loadingDates, setLoadingDates] = useState(false);
  const [bookingStats, setBookingStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [loadingQuote, setLoadingQuote] = useState(false);
  const [quoteData, setQuoteData] = useState(null);
  const [successModalVisible, setSuccessModalVisible] = useState(false);
  const [bookingId, setBookingId] = useState(null);
  const [submittedTime, setSubmittedTime] = useState(null);
  const [submittedDate, setSubmittedDate] = useState(null);

  const navigate = useNavigate();

  // Get URL parameters
  const quoteId = searchParams.get("quoteId");
  const customerPhone = searchParams.get("phone");
  const customerId = searchParams.get("customerId");

  useEffect(() => {
    // Pre-fill form if data available
    if (customerPhone) {
      form.setFieldsValue({ customerPhone });
    }
    if (customerId) {
      form.setFieldsValue({ customerId });
    }

    // Don't set default date - let user select their own date
    // Load time slots for today and check availability for next 7 days
    const today = dayjs();
    loadTimeSlots(today);
    loadBookingStats(today);
    checkDateAvailability();
    loadQuoteData();
  }, [customerPhone, customerId, form]);

  const checkDateAvailability = async () => {
    setLoadingDates(true);
    const availability = {};

    try {
      // Check availability for next 7 days
      for (let i = 0; i < 7; i++) {
        const date = dayjs().add(i, "day");
        const dateStr = date.format("YYYY-MM-DD");

        try {
          const result = await getAvailableTimeSlots(dateStr);
          availability[dateStr] =
            result.success && result.data && result.data.length > 0;
        } catch (error) {
          availability[dateStr] = false;
        }
      }

      setDateAvailability(availability);
    } catch (error) {
      logError("CallBookingPage: error checking date availability", error);
    } finally {
      setLoadingDates(false);
    }
  };

  const loadTimeSlots = async (date) => {
    if (!date) return;

    try {
      const result = await getAvailableTimeSlots(date.format("YYYY-MM-DD"));

      if (result.success) {
        setTimeSlots(result.data);
      } else {
        setTimeSlots([]);
        message.warning("No time slots available for this date");
      }
    } catch (error) {
      logError("CallBookingPage: error loading time slots", error);
      message.error("Error loading time slots");
      setTimeSlots([]);
    }
  };

  const loadBookingStats = async (date) => {
    if (!date) return;

    setLoadingStats(true);
    try {
      const result = await getBookingStats(date.format("YYYY-MM-DD"));

      if (result.success) {
        setBookingStats(result.data);
      } else {
        setBookingStats(null);
      }
    } catch (error) {
      logError("CallBookingPage: error loading booking stats", error);
      setBookingStats(null);
    } finally {
      setLoadingStats(false);
    }
  };

  const loadQuoteData = async () => {
    if (!quoteId) {
      setQuoteData(null);
      return;
    }

    setLoadingQuote(true);
    try {
      const result = await getQuoteData(quoteId);
      if (result.success && result.data) {
        setQuoteData(result.data);

        // Pre-fill form with quote data
        form.setFieldsValue({
          customerName: result.data.customerName || "",
          customerPhone: result.data.customerPhone || customerPhone || "",
          customerId: result.data.customerId || customerId || "",
        });
      } else {
        setQuoteData(null);
        message.warning("Could not load quote details.");
      }
    } catch (error) {
      logError("CallBookingPage: error loading quote data", error);
      setQuoteData(null);
      message.error("Error loading quote data. Please try again.");
    } finally {
      setLoadingQuote(false);
    }
  };

  const handleDateChange = (date) => {
    logDebug("CallBookingPage: date picker change", {
      date,
      formatted: date?.format("YYYY-MM-DD"),
    });
    setSelectedDate(date);
    setSelectedTime(null);

    // Update the form field value
    if (date) {
      form.setFieldsValue({ preferredDate: date });
      loadTimeSlots(date);
      loadBookingStats(date);
      // Also check if we need to update availability for this date
      const dateStr = date.format("YYYY-MM-DD");
      if (!dateAvailability[dateStr]) {
        checkDateAvailability();
      }
    }
  };

  const handleTimeSelect = (time) => {
    setSelectedTime(time);
    form.setFieldsValue({ preferredTime: time });
  };

  const handleSubmit = async (values) => {
    if (!selectedTime) {
      message.error("Please select a time slot");
      return;
    }

    setLoading(true);

    const formData = {
      quoteId: quoteId || "manual_booking",
      customerId: customerId || "manual_customer",
      customerPhone: values.customerPhone,
      customerName: values.customerName,
      preferredDate: values.preferredDate.format("YYYY-MM-DD"),
      preferredTime: selectedTime,
      notes: values.notes || "",
    };

    logDebug("CallBookingPage: form submission debug", {
      preferredDate: values.preferredDate,
      preferredTime: selectedTime,
      formattedDate: formData.preferredDate,
      formData,
    });

    try {
      const result = await createCallBooking(formData);

      if (result.success) {
        message.success("Call booked successfully!");
        setBookingId(result.data.bookingId);
        // Store the submitted date and time before resetting form
        logDebug("CallBookingPage: storing submitted values", {
          preferredDate: values.preferredDate,
          selectedTime,
        });
        setSubmittedDate(values.preferredDate);
        setSubmittedTime(selectedTime);
        setSuccessModalVisible(true);
        form.resetFields();
        setSelectedTime(null);
        setSelectedDate(null);
        // Show success state or redirect
      } else {
        message.error("Error: " + result.error);
      }
    } catch (error) {
      logError("CallBookingPage: error submitting form", error);
      message.error("Error submitting form. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSuccessModalOk = () => {
    setSuccessModalVisible(false);
    setBookingId(null);
    setSubmittedTime(null);
    setSubmittedDate(null);
    // Open Bubakii website in new tab
    window.open("https://bubakii.co.za/", "_blank");
  };

  const handleSuccessModalCancel = () => {
    setSuccessModalVisible(false);
    setBookingId(null);
    setSubmittedTime(null);
    setSubmittedDate(null);
  };

  const disabledDate = (current) => {
    // Disable past dates
    if (current && current < dayjs().startOf("day")) {
      return true;
    }

    // Disable dates with no available time slots
    const dateStr = current.format("YYYY-MM-DD");
    if (dateAvailability[dateStr] === false) {
      return true;
    }

    return false;
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        padding: "20px",
      }}
    >
      <div style={{ maxWidth: "600px", margin: "0 auto" }}>
        <Card
          title={
            <Space>
              <PhoneOutlined style={{ color: "#667eea" }} />
              <span>Book Your Call</span>
            </Space>
          }
          style={{
            borderRadius: "20px",
            boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
          }}
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={{
              customerPhone,
              customerId,
            }}
          >
            <Form.Item
              name="customerName"
              label="Full Name"
              disabled={loadingQuote}
              rules={[
                { required: true, message: "Please enter your full name" },
              ]}
            >
              <Input
                prefix={<UserOutlined />}
                placeholder="Enter your full name"
                size="large"
              />
            </Form.Item>

            <Form.Item
              name="customerPhone"
              label="Phone Number"
              rules={[
                { required: true, message: "Please enter your phone number" },
              ]}
            >
              <Input
                prefix={<PhoneOutlined />}
                placeholder="Enter your phone number"
                size="large"
                disabled={loadingQuote}
              />
            </Form.Item>

            {/* Quote Information Display */}
            {quoteData && (
              <div
                style={{
                  marginBottom: "20px",
                  padding: "15px",
                  background: "#f0f8ff",
                  borderRadius: "8px",
                  border: "1px solid #d1ecf1",
                }}
              >
                <h4 style={{ margin: "0 0 10px 0", color: "#0c5460" }}>
                  📋 Quote Details
                </h4>
                <div style={{ fontSize: "14px", color: "#0c5460" }}>
                  <div>
                    <strong>From:</strong> {quoteData.from}
                  </div>
                  <div>
                    <strong>To:</strong> {quoteData.to}
                  </div>
                  <div>
                    <strong>Date:</strong> {quoteData.date}
                  </div>
                  <div>
                    <strong>Status:</strong> {quoteData.status}
                  </div>
                </div>
              </div>
            )}

            {/* Loading indicator for quote data */}
            {loadingQuote && (
              <div
                style={{
                  marginBottom: "20px",
                  padding: "15px",
                  background: "#fff3cd",
                  borderRadius: "8px",
                  border: "1px solid #ffeaa7",
                  textAlign: "center",
                  color: "#856404",
                }}
              >
                🔄 Loading quote details...
              </div>
            )}

            <Form.Item
              name="preferredDate"
              label="Preferred Date"
              rules={[{ required: true, message: "Please select a date" }]}
            >
              <DatePicker
                style={{ width: "100%" }}
                size="large"
                disabledDate={disabledDate}
                onChange={handleDateChange}
                placeholder="Select date"
              />
              {loadingDates && (
                <div
                  style={{ marginTop: "8px", fontSize: "12px", color: "#666" }}
                >
                  🔍 Checking date availability...
                </div>
              )}
              <div
                style={{ marginTop: "8px", fontSize: "12px", color: "#666" }}
              >
                📅 Available dates are enabled, unavailable dates are grayed out
              </div>
            </Form.Item>

            {/* Booking Statistics */}
            {bookingStats && (
              <div
                style={{
                  background: "#f8f9fa",
                  padding: "16px",
                  borderRadius: "8px",
                  marginBottom: "16px",
                  border: "1px solid #e9ecef",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "8px",
                  }}
                >
                  <span style={{ fontWeight: "600", color: "#495057" }}>
                    📊 Slot Availability
                  </span>
                  {loadingStats && (
                    <span style={{ fontSize: "12px", color: "#6c757d" }}>
                      Loading...
                    </span>
                  )}
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(4, 1fr)",
                    gap: "16px",
                  }}
                >
                  <div style={{ textAlign: "center" }}>
                    <div
                      style={{
                        fontSize: "18px",
                        fontWeight: "bold",
                        color: "#28a745",
                      }}
                    >
                      {bookingStats.availableSlots}
                    </div>
                    <div style={{ fontSize: "12px", color: "#6c757d" }}>
                      Available
                    </div>
                  </div>
                  <div style={{ textAlign: "center" }}>
                    <div
                      style={{
                        fontSize: "18px",
                        fontWeight: "bold",
                        color: "#dc3545",
                      }}
                    >
                      {bookingStats.totalBookings}
                    </div>
                    <div style={{ fontSize: "12px", color: "#6c757d" }}>
                      Booked
                    </div>
                  </div>
                  <div style={{ textAlign: "center" }}>
                    <div
                      style={{
                        fontSize: "18px",
                        fontWeight: "bold",
                        color: "#007bff",
                      }}
                    >
                      {bookingStats.totalSlots}
                    </div>
                    <div style={{ fontSize: "12px", color: "#6c757d" }}>
                      Total
                    </div>
                  </div>
                  <div style={{ textAlign: "center" }}>
                    <div
                      style={{
                        fontSize: "18px",
                        fontWeight: "bold",
                        color: "#ffc107",
                      }}
                    >
                      {bookingStats.occupancyRate}%
                    </div>
                    <div style={{ fontSize: "12px", color: "#6c757d" }}>
                      Occupancy
                    </div>
                  </div>
                </div>
              </div>
            )}

            <Form.Item label="Available Time Slots" required>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(80px, 1fr))",
                  gap: "10px",
                  marginTop: "10px",
                }}
              >
                {timeSlots.length === 0 ? (
                  <div
                    style={{
                      gridColumn: "1 / -1",
                      textAlign: "center",
                      color: "#666",
                    }}
                  >
                    No time slots available for this date
                  </div>
                ) : (
                  timeSlots.map((time) => (
                    <Button
                      key={time}
                      type={selectedTime === time ? "primary" : "default"}
                      onClick={() => handleTimeSelect(time)}
                      style={{
                        height: "40px",
                        borderRadius: "8px",
                        border:
                          selectedTime === time ? "none" : "2px solid #e1e5e9",
                      }}
                    >
                      {time}
                    </Button>
                  ))
                )}
              </div>
              <div
                style={{ marginTop: "8px", fontSize: "12px", color: "#666" }}
              >
                💡 <strong>Real-time availability:</strong> Slots are updated in
                real-time. If someone books a slot while you&apos;re filling
                this form, it will automatically become unavailable.
              </div>
            </Form.Item>

            <Form.Item name="notes" label="Additional Notes">
              <TextArea
                prefix={<FileTextOutlined />}
                rows={3}
                placeholder="Any special requirements or questions?"
                size="large"
              />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                size="large"
                style={{
                  width: "100%",
                  height: "50px",
                  borderRadius: "10px",
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  border: "none",
                }}
              >
                {loading ? "Booking..." : "Book My Call"}
              </Button>
            </Form.Item>
          </Form>

          {quoteId && (
            <div
              style={{
                marginTop: "20px",
                padding: "15px",
                background: "#f8f9ff",
                borderRadius: "8px",
                border: "1px solid #e1e5e9",
              }}
            >
              <strong>Quote Reference:</strong> {quoteId}
            </div>
          )}
        </Card>
      </div>

      {/* Success Modal */}
      <Modal
        title={
          <div style={{ textAlign: "center" }}>
            <CheckCircleOutlined
              style={{ color: "#52c41a", fontSize: "24px", marginRight: "8px" }}
            />
            Booking Successful!
          </div>
        }
        open={successModalVisible}
        onOk={handleSuccessModalOk}
        onCancel={handleSuccessModalCancel}
        footer={[
          <Button
            key="check-status"
            icon={<EyeOutlined />}
            onClick={() =>
              message.info(
                "Status checking functionality will be implemented soon!"
              )
            }
          >
            Check Status
          </Button>,
          <Button
            key="close"
            icon={<CloseOutlined />}
            onClick={handleSuccessModalCancel}
          >
            Close
          </Button>,
          <Button
            key="home"
            type="primary"
            icon={<HomeOutlined />}
            onClick={() => navigate("/dashboard")}
          >
            Return to Home
          </Button>,
        ]}
        width={500}
        centered
      >
        <div style={{ textAlign: "center", padding: "20px 0" }}>
          <p style={{ fontSize: "16px", marginBottom: "16px" }}>
            Your call booking has been confirmed successfully!
          </p>

          {bookingId && (
            <div
              style={{
                background: "#f6ffed",
                border: "1px solid #b7eb8f",
                borderRadius: "8px",
                padding: "12px",
                marginBottom: "16px",
              }}
            >
              <strong>Booking ID:</strong> {bookingId}
            </div>
          )}

          <div
            style={{
              background: "#f0f8ff",
              border: "1px solid #91d5ff",
              borderRadius: "8px",
              padding: "12px",
              marginBottom: "16px",
              textAlign: "left",
            }}
          >
            <div>
              <strong>Date:</strong>{" "}
              {submittedDate?.format("YYYY-MM-DD") || "Not specified"}
            </div>
            <div>
              <strong>Time:</strong> {submittedTime || "Not specified"}
            </div>
            <div>
              <strong>Status:</strong>{" "}
              <span style={{ color: "#52c41a" }}>Confirmed</span>
            </div>
          </div>

          <p style={{ color: "#666", fontSize: "14px" }}>
            You will receive a confirmation shortly. You can check the status of
            your booking or return to the home page.
          </p>
        </div>
      </Modal>
    </div>
  );
};

export default CallBookingPage;
